/**
 * Render-ready form for capability inputs.
 *
 * Consumes the FieldSpec[] tree the backend ships at
 * `GET /capabilities/{id}/schema` — no JSON Schema parsing here. Each spec
 * maps to an appropriate input (text / number / checkbox / select / array
 * editor / nested fieldset). State is held as a single structured object
 * by the parent and threaded down via `value`/`onChange` per field.
 *
 * `any` falls back to a small JSON textarea so we don't block on unusual
 * shapes — but everything Pydantic emits today is covered by the dedicated
 * controls.
 */
import { useEffect, useId, useRef, useState } from 'react';

import type { FieldSpec } from '@optum/api-client/types';

import { Checkbox } from '../../ui/Checkbox.js';
import { Icon, Input, Textarea } from '../../ui/finixt.js';
import { Select } from '../../ui/Select.js';

// ── Public entry point ───────────────────────────────────────────────────

export interface SchemaFormProps {
  fields: FieldSpec[];
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}

export function SchemaForm({ fields, value, onChange }: SchemaFormProps) {
  if (fields.length === 0) {
    return (
      <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
        No input fields — the capability accepts an empty object <code>{'{}'}</code>.
      </p>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      {fields.map((spec) => (
        <FieldRow
          key={spec.name}
          spec={spec}
          value={value[spec.name]}
          siblings={value}
          onChange={(v) => {
            const next = { ...value };
            if (v === undefined) delete next[spec.name];
            else next[spec.name] = v;
            cascadeClearDependents(fields, next, spec.name);
            onChange(next);
          }}
        />
      ))}
    </div>
  );
}

/**
 * When a discriminator field changes, drop any dependent field whose
 * current value is no longer valid for the new sibling. Mutates `record`.
 * Same-level only — discriminators in nested objects/array items are
 * resolved by the nested SchemaForm that renders them.
 */
function cascadeClearDependents(
  fields: FieldSpec[],
  record: Record<string, unknown>,
  changedField: string,
): void {
  for (const f of fields) {
    if (!f.depends_on || !f.options_by || f.depends_on !== changedField) continue;
    const siblingValue = record[f.depends_on];
    const allowed = f.options_by[String(siblingValue)] ?? [];
    const current = record[f.name];
    if (current == null) continue;
    if (!allowed.some((v) => String(v) === String(current))) {
      delete record[f.name];
    }
  }
}

/** Whether this field is one the reset affordances act on. */
function isResettable(spec: FieldSpec): boolean {
  // `has_default`, never the truthiness of `default` — false, 0, '' and null
  // are all values a capability may legitimately declare.
  return !spec.required && spec.has_default;
}

/** Whether anything in the tree declares an optional default worth restoring. */
export function hasResettableOptional(fields: FieldSpec[]): boolean {
  return fields.some(
    (f) =>
      isResettable(f) ||
      (f.type === 'object' && (f.children?.length ?? 0) > 0 && hasResettableOptional(f.children!)),
  );
}

/**
 * `value` with every optional field that declares a default put back to it.
 *
 * Overwrites rather than filling blanks: the point is to get back to what the
 * capability declares, whatever the form currently holds. Recurses into
 * nested objects, since their children are declared too. Array items are left
 * alone — the schema describes the element shape, not how many there should
 * be, so there is no declared value to restore them to.
 */
export function withOptionalDefaults(
  fields: FieldSpec[],
  value: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...value };
  for (const spec of fields) {
    if (isResettable(spec)) {
      out[spec.name] = spec.default;
      continue;
    }
    if (spec.type === 'object' && (spec.children?.length ?? 0) > 0) {
      const current = out[spec.name];
      const nested =
        current && typeof current === 'object' && !Array.isArray(current)
          ? (current as Record<string, unknown>)
          : {};
      out[spec.name] = withOptionalDefaults(spec.children!, nested);
    }
  }
  return out;
}

/** Build a default value object from the field tree — required filled, optionals omitted. */
export function defaultsForFields(fields: FieldSpec[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (!f.required) continue;
    const v = defaultFor(f);
    if (v === undefined) continue;
    out[f.name] = v;
  }
  return out;
}

// ── Per-field row (label + meta + control) ──────────────────────────────

function FieldRow({
  spec,
  value,
  siblings,
  onChange,
}: {
  spec: FieldSpec;
  value: unknown;
  siblings: Record<string, unknown>;
  onChange: (next: unknown) => void;
}) {
  const id = useId();
  const constraints = describeConstraints(spec);
  const inlineCheckbox = spec.type === 'boolean';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          columnGap: 7,
          rowGap: 5,
        }}
      >
        {inlineCheckbox && (
          <Checkbox
            id={id}
            checked={effectiveBool(value, spec)}
            onChange={(checked) => onChange(checked)}
          />
        )}
        <label
          htmlFor={id}
          style={{
            fontSize: 'var(--text-body-md)',
            fontWeight: 'var(--weight-medium)',
            color: 'var(--text-strong)',
          }}
          className="mono"
        >
          {spec.name}
        </label>
        <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
          {typeLabel(spec)}
          {spec.nullable && <span style={{ color: 'var(--warn)' }}> | null</span>}
        </span>
        {spec.required ? (
          <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--danger)' }}>required</span>
        ) : (
          <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
            optional
          </span>
        )}
        {constraints.map((c) => (
          <span
            key={c}
            style={{
              borderRadius: 'var(--radius-chip)',
              border: '1px solid var(--border-hairline)',
              paddingLeft: 7,
              paddingRight: 7,
              paddingTop: 5,
              paddingBottom: 5,
              fontSize: 'var(--text-meta-lg)',
              color: 'var(--text-muted)',
            }}
            className="mono"
          >
            {c}
          </span>
        ))}
        {/* Only where the schema declares a default to go back to. A required
            field has nothing to reset to, and an optional one without a
            declared default would reset to a value we invented. Tested on
            `has_default`, never on `default` — false, 0, '' and null are all
            legitimate declared defaults. */}
        {isResettable(spec) && (
          <button
            // Without this it is a submit button, and this form runs the task.
            type="button"
            onClick={() => onChange(spec.default)}
            title={`Reset to default: ${JSON.stringify(spec.default)}`}
            aria-label={`Reset ${spec.name} to its default`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 'var(--radius-chip)',
              border: 'none',
              background: 'transparent',
              padding: 5,
              color: 'var(--text-faint)',
            }}
          >
            <Icon name="restart_alt" size={14} />
          </button>
        )}
      </div>
      {spec.description && (
        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
          {spec.description}
        </p>
      )}
      {!inlineCheckbox && (
        <FieldControl id={id} spec={spec} value={value} siblings={siblings} onChange={onChange} />
      )}
    </div>
  );
}

// ── Type-dispatched control ─────────────────────────────────────────────

function FieldControl({
  id,
  spec,
  value,
  siblings,
  onChange,
}: {
  id: string;
  spec: FieldSpec;
  value: unknown;
  siblings: Record<string, unknown>;
  onChange: (next: unknown) => void;
}) {
  // Enum first — wins over the underlying primitive type tag.
  if (spec.type === 'enum' && spec.options) {
    // Conditional dropdown: filter options by the current value of the
    // sibling discriminator. Fall back to the full union (spec.options)
    // when the sibling is unset or carries a value not in the mapping.
    let options = spec.options;
    if (spec.depends_on && spec.options_by) {
      const siblingValue = siblings[spec.depends_on];
      const subset = spec.options_by[String(siblingValue)];
      options = subset ?? [];
    }
    return (
      <Select
        id={id}
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(v) => {
          onChange(v === '__null' ? null : v);
        }}
        options={[
          { value: '', label: '—' },
          ...(spec.nullable ? [{ value: '__null', label: '(null)' }] : []),
          ...options.map((opt) => ({ value: String(opt), label: String(opt) })),
        ]}
      />
    );
  }

  switch (spec.type) {
    case 'boolean':
      return (
        <Checkbox
          id={id}
          checked={effectiveBool(value, spec)}
          onChange={(checked) => onChange(checked)}
        />
      );

    case 'integer':
    case 'number':
      return (
        <Input
          id={id}
          type="number"
          value={value === undefined || value === null ? '' : String(value)}
          step={spec.type === 'integer' ? 1 : 'any'}
          {...(spec.minimum !== null ? { min: spec.minimum } : {})}
          {...(spec.maximum !== null ? { max: spec.maximum } : {})}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onChange(spec.nullable ? null : undefined);
              return;
            }
            const parsed = spec.type === 'integer' ? parseInt(raw, 10) : Number(raw);
            onChange(Number.isNaN(parsed) ? raw : parsed);
          }}
        />
      );

    case 'string': {
      const isLong = (spec.max_length ?? 0) > 200;
      const common = {
        id,
        value: value === undefined || value === null ? '' : String(value),
        ...(spec.min_length !== null ? { minLength: spec.min_length } : {}),
        ...(spec.max_length !== null ? { maxLength: spec.max_length } : {}),
      };
      if (isLong) {
        return (
          <Textarea
            {...common}
            rows={6}
            spellCheck={false}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      }
      return <Input {...common} onChange={(e) => onChange(e.target.value)} />;
    }

    case 'datetime':
      return (
        <Input
          id={id}
          type="datetime-local"
          value={toDatetimeLocal(value)}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) {
              onChange(spec.nullable ? null : undefined);
              return;
            }
            // Browser gives "YYYY-MM-DDTHH:mm" (no tz) — append :00Z so the
            // backend parses it as a real ISO datetime.
            onChange(`${v}:00Z`);
          }}
        />
      );

    case 'object':
      // Free-form dict (e.g. `dict[str, Any]`) has no declared children —
      // fall back to a raw JSON editor so arbitrary keys can be entered.
      if (!spec.children || spec.children.length === 0) {
        return <RawJsonField value={value} onChange={onChange} />;
      }
      return (
        <div
          style={{
            borderRadius: 'var(--radius-chip)',
            border: '1px solid var(--border-hairline)',
            background: 'var(--surface-card)',
            padding: 11,
          }}
        >
          <SchemaForm
            fields={spec.children}
            value={isPlainObject(value) ? value : {}}
            onChange={(next) => onChange(next)}
          />
        </div>
      );

    case 'array':
      return <ArrayField spec={spec} value={value} onChange={onChange} />;

    case 'any':
    default:
      return <RawJsonField value={value} onChange={onChange} />;
  }
}

// ── Array editor ────────────────────────────────────────────────────────

function ArrayField({
  spec,
  value,
  onChange,
}: {
  spec: FieldSpec;
  value: unknown;
  onChange: (next: unknown) => void;
}) {
  const items: unknown[] = Array.isArray(value) ? value : [];
  const itemSpec = spec.item;
  if (!itemSpec) return <RawJsonField value={value} onChange={onChange} />;

  function setItem(i: number, v: unknown) {
    const next = [...items];
    next[i] = v;
    onChange(next);
  }
  function removeItem(i: number) {
    onChange(items.filter((_, j) => j !== i));
  }
  function addItem() {
    onChange([...items, defaultFor(itemSpec!)]);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {items.length === 0 && (
        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
          (empty list — click below to add)
        </p>
      )}
      {items.map((it, i) => (
        <div
          key={i}
          style={{
            borderRadius: 'var(--radius-chip)',
            border: '1px solid var(--border-hairline)',
            background: 'var(--surface-card)',
            padding: 11,
          }}
        >
          <div
            style={{
              marginBottom: 7,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span
              style={{
                fontSize: 'var(--text-meta-lg)',
                fontWeight: 'var(--weight-medium)',
                color: 'var(--text-muted)',
              }}
            >
              #{i}
            </span>
            <button
              type="button"
              onClick={() => removeItem(i)}
              style={{
                borderRadius: 'var(--radius-chip)',
                border: '1px solid var(--border-hairline)',
                paddingLeft: 7,
                paddingRight: 7,
                paddingTop: 5,
                paddingBottom: 5,
                fontSize: 'var(--text-meta-lg)',
                color: 'var(--text-muted)',
              }}
            >
              Remove
            </button>
          </div>
          {itemSpec.type === 'object' && itemSpec.children && itemSpec.children.length > 0 ? (
            <SchemaForm
              fields={itemSpec.children}
              value={isPlainObject(it) ? it : {}}
              onChange={(v) => setItem(i, v)}
            />
          ) : (
            // Scalars and free-form dicts route through FieldControl, which
            // renders the raw JSON editor for a childless object item.
            <FieldControl
              id={`array-item-${i}`}
              spec={itemSpec}
              value={it}
              siblings={{}}
              onChange={(v) => setItem(i, v)}
            />
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        style={{
          borderRadius: 'var(--radius-chip)',
          border: '1px dashed var(--border-hairline)',
          background: 'transparent',
          padding: '5px 11px',
          fontSize: 'var(--text-meta-lg)',
          color: 'var(--text-accent)',
          cursor: 'pointer',
        }}
      >
        + Add item
      </button>
    </div>
  );
}

// ── Raw JSON fallback for `any` typed fields ────────────────────────────

function RawJsonField({ value, onChange }: { value: unknown; onChange: (next: unknown) => void }) {
  const [text, setText] = useState(() => serialize(value));
  const [err, setErr] = useState<string | null>(null);
  // The last value this textarea produced. Anything else arriving in `value`
  // came from outside — the reset icon, or the parent reseeding on a
  // capability change — and the text has to follow it. Comparing against our
  // own last emission rather than against `text` is what stops this from
  // fighting the user mid-keystroke, and from reformatting what they typed.
  const emitted = useRef<unknown>(value);
  useEffect(() => {
    if (Object.is(value, emitted.current)) return;
    emitted.current = value;
    setText(serialize(value));
    setErr(null);
  }, [value]);

  const emit = (next: unknown) => {
    emitted.current = next;
    onChange(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <Textarea
        rows={4}
        spellCheck={false}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (e.target.value.trim() === '') {
            setErr(null);
            emit(undefined);
            return;
          }
          try {
            emit(JSON.parse(e.target.value));
            setErr(null);
          } catch (parseErr) {
            setErr(parseErr instanceof Error ? parseErr.message : String(parseErr));
          }
        }}
        className="mono"
        style={{ fontSize: 'var(--text-meta-lg)' }}
      />
      {err && <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--danger)' }}>{err}</p>}
    </div>
  );
}

function serialize(value: unknown): string {
  return value === undefined ? '' : JSON.stringify(value, null, 2);
}

// ── Helpers ─────────────────────────────────────────────────────────────

function typeLabel(spec: FieldSpec): string {
  if (spec.type === 'array' && spec.item) return `${spec.item.type}[]`;
  return spec.type;
}

/** Checkbox state for a boolean field: an untouched field (undefined, omitted
 *  from the payload) shows its schema default — the value the backend applies. */
function effectiveBool(value: unknown, spec: FieldSpec): boolean {
  if (value === undefined && spec.has_default) return Boolean(spec.default);
  return Boolean(value);
}

function describeConstraints(spec: FieldSpec): string[] {
  const out: string[] = [];
  if (spec.min_length !== null || spec.max_length !== null) {
    out.push(`len ${spec.min_length ?? 0}..${spec.max_length ?? '∞'}`);
  }
  const lo =
    spec.exclusive_minimum !== null
      ? `>${spec.exclusive_minimum}`
      : spec.minimum !== null
        ? `≥${spec.minimum}`
        : '';
  const hi =
    spec.exclusive_maximum !== null
      ? `<${spec.exclusive_maximum}`
      : spec.maximum !== null
        ? `≤${spec.maximum}`
        : '';
  const range = [lo, hi].filter(Boolean).join(' ');
  if (range) out.push(range);
  if (spec.has_default) out.push(`default: ${JSON.stringify(spec.default)}`);
  return out;
}

function defaultFor(spec: FieldSpec): unknown {
  if (spec.has_default) return spec.default;
  // Conditional dropdown: leave unset — the union isn't a meaningful pick,
  // the user has to choose the discriminator first. cascadeClearDependents
  // also keeps it cleared when the sibling changes later.
  if (spec.depends_on) return undefined;
  if (spec.type === 'enum' && spec.options && spec.options.length > 0) return spec.options[0];
  switch (spec.type) {
    case 'string':
    case 'datetime':
      return '';
    case 'integer':
    case 'number':
      return 0;
    case 'boolean':
      return false;
    case 'array':
      return [];
    case 'object': {
      const out: Record<string, unknown> = {};
      for (const child of spec.children ?? []) {
        if (child.required) out[child.name] = defaultFor(child);
      }
      return out;
    }
    default:
      return null;
  }
}

function toDatetimeLocal(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  // Trim trailing Z + seconds for the <input type=datetime-local> format.
  const m = v.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
  return m ? m[1]! : '';
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
